import { useMemo, useState } from "react";
import { Calculator as CalcIcon, Package, Layers, Ruler, Truck } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { PRODUCTS, COMPANY, ROLLS_PER_PALLET } from "../../data/products";

export default function Calculator() {
  const [productId, setProductId] = useState("035");
  const [thickness, setThickness] = useState("100");
  const [area, setArea] = useState("100");
  const [waste, setWaste] = useState("10");

  const product = useMemo(
    () => PRODUCTS.find((p) => p.id === productId),
    [productId]
  );
  const variant = useMemo(
    () => product?.variants.find((v) => String(v.thickness) === String(thickness)),
    [product, thickness]
  );

  // If selected thickness not available for new product, reset
  const availableThicknesses = product?.variants.map((v) => String(v.thickness)) || [];
  const effectiveThickness = availableThicknesses.includes(thickness)
    ? thickness
    : availableThicknesses[0];

  const result = useMemo(() => {
    const a = parseFloat(area) || 0;
    const w = (parseFloat(waste) || 0) / 100;
    if (!variant || a <= 0) {
      return { rolls: 0, areaTotal: 0, volume: 0, rollArea: 0, pallets: 0, freeDelivery: false };
    }
    const rollArea = variant.areaPerRoll; // m² real value from supplier data
    const areaTotal = a * (1 + w);
    const rolls = Math.ceil(areaTotal / rollArea);
    const volume = (rolls * rollArea * variant.thickness) / 1000; // m³
    const pallets = Math.ceil(rolls / ROLLS_PER_PALLET);
    const freeDelivery = pallets >= COMPANY.freeDeliveryFromPallets;
    return {
      rolls,
      areaTotal: areaTotal.toFixed(1),
      volume: volume.toFixed(2),
      rollArea: rollArea.toFixed(2),
      pallets,
      freeDelivery,
    };
  }, [area, waste, variant]);

  return (
    <section
      id="kalkulator"
      data-testid="calculator-section"
      className="bg-zinc-950 text-white py-20 lg:py-28 relative overflow-hidden"
    >
      {/* Decorative stripes */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-stripes" />
      <div className="absolute -left-20 top-1/2 -translate-y-1/2 h-72 w-72 border-2 border-red-600 rotate-45 opacity-20 hidden lg:block" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-[2px] w-10 bg-red-500" />
            <span className="font-tech text-xs tracking-[0.3em] uppercase font-bold text-red-500">
              03 · Kalkulator zapotrzebowania
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none tracking-tighter">
            Oblicz ile<br />
            wełny <span className="text-red-500">potrzebujesz.</span>
          </h2>
          <p className="mt-5 text-lg text-zinc-300 max-w-2xl">
            Podaj powierzchnię do izolacji, wybierz produkt i grubość — policzymy
            liczbę rolek i objętość materiału.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-0 border-2 border-red-600">
          {/* Inputs */}
          <div className="lg:col-span-3 bg-zinc-900 p-8 lg:p-10 space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <CalcIcon className="h-5 w-5 text-red-500" />
              <span className="font-tech text-sm uppercase tracking-widest text-zinc-400">
                Parametry
              </span>
            </div>

            <Field label="Powierzchnia do ocieplenia (m²)">
              <Input
                type="number"
                min="1"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                data-testid="calculator-area-input"
                className="rounded-none bg-black border-2 border-zinc-700 focus:border-red-500 text-white text-lg h-14 font-tech"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Wariant produktu (λ)">
                <Select value={productId} onValueChange={setProductId}>
                  <SelectTrigger
                    data-testid="calculator-variant-select"
                    className="rounded-none bg-black border-2 border-zinc-700 text-white h-14 font-tech"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {PRODUCTS.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="font-tech">
                        Wełna Szklana {p.code} W/m·K
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Grubość (mm)">
                <Select
                  value={effectiveThickness}
                  onValueChange={setThickness}
                >
                  <SelectTrigger
                    data-testid="calculator-thickness-select"
                    className="rounded-none bg-black border-2 border-zinc-700 text-white h-14 font-tech"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {availableThicknesses.map((t) => (
                      <SelectItem key={t} value={t} className="font-tech">
                        {t} mm
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Zapas na docinanie (%)">
              <Input
                type="number"
                min="0"
                max="50"
                value={waste}
                onChange={(e) => setWaste(e.target.value)}
                data-testid="calculator-waste-input"
                className="rounded-none bg-black border-2 border-zinc-700 focus:border-red-500 text-white text-lg h-14 font-tech"
              />
            </Field>

            <p className="text-xs text-zinc-500 font-tech uppercase tracking-widest pt-2">
              * Kalkulator służy do wstępnego szacowania. Skontaktuj się z nami w sprawie wyceny.
            </p>
          </div>

          {/* Results */}
          <div
            className="lg:col-span-2 bg-red-600 p-8 lg:p-10 text-white flex flex-col justify-between"
            data-testid="calculator-result"
          >
            <div>
              <div className="font-tech text-xs uppercase tracking-[0.3em] text-red-200 mb-1">
                Wynik kalkulacji
              </div>
              <div className="font-display text-xl mt-1 mb-8">
                {product?.name}
              </div>

              <ResultRow
                icon={Package}
                label="Liczba rolek"
                value={result.rolls}
                unit="szt."
                testid="calculator-result-rolls"
                big
              />
              <ResultRow
                icon={Truck}
                label="Liczba palet"
                value={result.pallets}
                unit="palet"
                testid="calculator-result-pallets"
              />
              <ResultRow
                icon={Layers}
                label="Powierzchnia z zapasem"
                value={result.areaTotal}
                unit="m²"
                testid="calculator-result-area"
              />
              <ResultRow
                icon={Ruler}
                label="Objętość materiału"
                value={result.volume}
                unit="m³"
                testid="calculator-result-volume"
              />

              {/* Free delivery indicator */}
              <div
                data-testid="calculator-free-delivery"
                className={`mt-6 p-4 border-2 ${
                  result.freeDelivery
                    ? "border-white bg-black"
                    : "border-red-300 bg-red-700"
                }`}
              >
                <div className="flex items-start gap-3">
                  <Truck className="h-6 w-6 text-white shrink-0" />
                  <div>
                    <div className="font-display text-base text-white uppercase tracking-tight leading-tight">
                      {result.freeDelivery
                        ? "✓ Dostawa GRATIS!"
                        : `Dostawa GRATIS od ${COMPANY.freeDeliveryFromPallets} palet`}
                    </div>
                    <div className="font-tech text-[11px] uppercase tracking-widest text-red-100 mt-1">
                      {result.freeDelivery
                        ? "Twoje zamówienie kwalifikuje się"
                        : `Brakuje: ${Math.max(0, COMPANY.freeDeliveryFromPallets - result.pallets)} palet`}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <a
              href="#kontakt"
              data-testid="calculator-cta-quote"
              className="mt-6 inline-flex items-center justify-center w-full bg-black hover:bg-zinc-800 text-white font-bold uppercase tracking-wider h-14 transition"
            >
              Zamów te ilości →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="font-tech text-xs uppercase tracking-widest text-zinc-400 mb-2 block">
        {label}
      </Label>
      {children}
    </div>
  );
}

function ResultRow({ icon: Icon, label, value, unit, testid, big }) {
  return (
    <div className="border-t border-red-400/40 py-4 first:border-t-0 first:pt-0 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="h-4 w-4 text-red-200" />}
        <span className="font-tech text-xs uppercase tracking-widest text-red-100">
          {label}
        </span>
      </div>
      <div data-testid={testid} className="text-right">
        <span
          className={`font-display ${big ? "text-5xl" : "text-2xl"} text-white leading-none`}
        >
          {value}
        </span>
        <span className="font-tech text-xs uppercase tracking-widest text-red-200 ml-2">
          {unit}
        </span>
      </div>
    </div>
  );
}
